/**
 * Client-side mirror of the SEO rules that depend only on the head fields, so the SEO card updates
 * while the admin types. Keep the ranges, messages and severities in step with SeoLinter.java; the
 * server stays the source of truth (it re-checks on diff and in the site report).
 */

export const TITLE_RANGE = [30, 60];
export const DESCRIPTION_RANGE = [70, 160];

const TITLE_LABELS = ['Title', 'Social title (og:title)', 'Twitter title'];
const DESCRIPTION_LABELS = ['Description', 'Social description (og:description)', 'Twitter description'];

/** Rules recomputed live; server findings for these are replaced by the live ones. */
export const LIVE_RULES = new Set([
  'TITLE_MISSING',
  'TITLE_LENGTH',
  'TITLE_META_MISMATCH',
  'DESCRIPTION_MISSING',
  'DESCRIPTION_LENGTH',
  'DESCRIPTION_META_MISMATCH',
]);

export const SEVERITY_ORDER = { ERROR: 0, WARN: 1, INFO: 2 };

const normalize = (v) => (v || '').replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
const withoutBrand = (t) => t.replace(/\s*[|–—-]\s*Team Today$/, '');

const finding = (rule, severity, title, message, unitId = null) => ({ rule, severity, title, message, unitId });

const mismatch = (units, valueOf, labels, rule, title) => {
  const main = units.find((u) => u.group === 'head' && u.label === labels[0]);
  if (!main) return [];
  const expected = withoutBrand(normalize(valueOf(main)));
  const differing = labels
    .slice(1)
    .map((l) => units.find((u) => u.group === 'head' && u.label === l))
    .filter((u) => u && withoutBrand(normalize(valueOf(u))) !== expected);
  if (differing.length === 0) return [];
  const names = differing.map((u) => u.label).join(' and ');
  return [
    finding(
      rule,
      'INFO',
      title,
      `${names} differ${differing.length === 1 ? 's' : ''} from the ${labels[0].toLowerCase()}, so shares show different text.`,
      differing[0].id
    ),
  ];
};

/** The page's findings with the head-field rules recomputed from the current (edited) values. */
export const liveFindings = (units, valueOf, serverFindings) => {
  const server = serverFindings || [];
  const noindex = server.some((f) => f.rule === 'NOINDEX');
  const out = server.filter((f) => !LIVE_RULES.has(f.rule));

  const title = units.find((u) => u.group === 'head' && u.label === 'Title');
  const titleText = normalize(title ? valueOf(title) : '');
  if (!titleText) {
    out.push(finding('TITLE_MISSING', 'ERROR', 'Missing title', 'The page has no <title>.'));
  } else if (!noindex && (titleText.length < TITLE_RANGE[0] || titleText.length > TITLE_RANGE[1])) {
    out.push(
      finding(
        'TITLE_LENGTH',
        'INFO',
        'Title length',
        `Title is ${titleText.length} characters; aim for ${TITLE_RANGE[0]}-${TITLE_RANGE[1]} so search results show it in full.`,
        title?.id
      )
    );
  }
  out.push(...mismatch(units, valueOf, TITLE_LABELS, 'TITLE_META_MISMATCH', 'Social titles differ from the title'));

  const description = units.find((u) => u.group === 'head' && u.label === 'Description');
  const descriptionText = normalize(description ? valueOf(description) : '');
  if (!descriptionText) {
    if (!noindex) out.push(finding('DESCRIPTION_MISSING', 'ERROR', 'Missing description', 'The page has no meta description.', description?.id));
  } else if (!noindex && (descriptionText.length < DESCRIPTION_RANGE[0] || descriptionText.length > DESCRIPTION_RANGE[1])) {
    out.push(
      finding(
        'DESCRIPTION_LENGTH',
        'INFO',
        'Description length',
        `Description is ${descriptionText.length} characters; aim for ${DESCRIPTION_RANGE[0]}-${DESCRIPTION_RANGE[1]}.`,
        description?.id
      )
    );
  }
  out.push(
    ...mismatch(units, valueOf, DESCRIPTION_LABELS, 'DESCRIPTION_META_MISMATCH', 'Social descriptions differ from the description')
  );

  return out.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
};

export const countBySeverity = (findings) =>
  findings.reduce((acc, f) => ({ ...acc, [f.severity]: (acc[f.severity] || 0) + 1 }), { ERROR: 0, WARN: 0, INFO: 0 });

export const severitySummary = (counts) => {
  const parts = [];
  if (counts.ERROR) parts.push(`${counts.ERROR} error${counts.ERROR === 1 ? '' : 's'}`);
  if (counts.WARN) parts.push(`${counts.WARN} warning${counts.WARN === 1 ? '' : 's'}`);
  if (counts.INFO) parts.push(`${counts.INFO} note${counts.INFO === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : 'No issues';
};
